import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal, Overline } from "../components/Reveal";
import { CheckCircle2 } from "lucide-react";
import { CESSNA } from "../data";

export default function CessnaPage({ onApply }) {
  return (
    <>
      <PageHero media="/videos/clip4.mp4" overline="Cessna Pilot Center" title="Africa's certified" highlight="Cessna Pilot Center." subtitle="Train under the manufacturer-backed, globally standardised Cessna Pilot Center programme — exclusive to Eagle 1 on the African continent." />

      <section className="py-24 lg:py-32 bg-white" data-testid="cessna-intro">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-2xl bg-[var(--paper)] p-10 card-shadow inline-flex w-full justify-center">
                <img src="/img/cpc.webp" alt="Cessna Pilot Center by Textron Aviation" className="w-full max-w-xs object-contain" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><Overline>The Distinction</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)] leading-[1.05]">{CESSNA.headline}</h2></Reveal>
            <Reveal delay={0.15}><p className="mt-5 text-[var(--muted)] text-lg leading-relaxed">{CESSNA.intro}</p></Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-white" data-testid="cessna-points">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 gap-6">
          {CESSNA.points.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="rounded-2xl border border-[var(--line)] p-8 card-shadow h-full flex gap-4">
                <CheckCircle2 size={22} className="text-accent shrink-0 mt-1" />
                <div><h3 className="font-display text-xl font-semibold text-[var(--primary)]">{p.title}</h3><p className="mt-2 text-[var(--muted)] leading-relaxed">{p.text}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-navy relative overflow-hidden" data-testid="what-is-cpc">
        <div className="absolute inset-0 grain" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl">
            <Reveal><Overline dark>What is a Cessna Pilot Center?</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tight text-white leading-[1.15]">
                A Cessna Pilot Center is a flight school hand-picked and accredited by <span className="text-accent-2">Textron Aviation</span> — the maker of Cessna aircraft — to deliver its official, standardised pilot-training programme.
              </h2>
            </Reveal>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "What it is", d: "A worldwide network of trusted schools that teach using Cessna's own integrated syllabus, training materials and King Schools courseware — the same proven system, wherever you fly." },
              { n: "02", t: "What certification means", d: "Being certified means Textron Aviation has vetted our instructors, aircraft, facilities and standards, and authorises us to deliver the CPC curriculum. It is an endorsement, not a formality." },
              { n: "03", t: "Why it matters", d: "You train on a manufacturer-backed programme with consistent, measurable milestones — giving employers and examiners confidence in the quality and structure behind your licence." },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="glass rounded-2xl p-8 h-full">
                  <div className="font-display text-5xl font-light text-accent-2 opacity-40">{c.n}</div>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{c.t}</h3>
                  <p className="mt-3 text-white/65 text-sm leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-12 max-w-3xl text-white/70 text-lg leading-relaxed border-l-2 border-[var(--secondary-2)] pl-6">
              For a student, choosing {"Africa's"} certified Cessna Pilot Center means learning from the source — a curriculum designed by the people who build the aeroplane {"you'll"} master, held to a global standard that follows you throughout your aviation career.
            </p>
          </Reveal>
        </div>
      </section>
      <CTABand onApply={onApply} title="Train with Africa's certified Cessna Pilot Center." text="Join a manufacturer-backed programme trusted by the world's leading flight schools." />
    </>
  );
}
